import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import JsonLd from "@/components/base/JsonLd";
import PageHero from "@/components/feature/PageHero";
import SiteLayout from "@/components/feature/SiteLayout";
import IntroSection from "@/pages/about/components/IntroSection";
import TimelineSection from "@/pages/about/components/TimelineSection";
import PrincipleSection from "@/pages/about/components/PrincipleSection";
import VoiceSection from "@/pages/about/components/VoiceSection";
import AboutFaq from "@/pages/about/components/AboutFaq";
import { aboutFaqList } from "@/mocks/faq";
import { siteInfo } from "@/mocks/site";

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: `关于${siteInfo.name}`,
  description: `${siteInfo.name}的品牌介绍、网站定位、内容原则与用户口碑。`,
  inLanguage: "zh-CN",
  url: "https://dearyuan.cn/about",
  publisher: {
    "@type": "Organization",
    name: siteInfo.name,
    email: siteInfo.email,
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: aboutFaqList.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "首页", item: "https://dearyuan.cn/" },
    { "@type": "ListItem", position: 2, name: "关于我们" },
  ],
};

export default function About() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    const timer = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  return (
    <>
      <PageMeta
        title="关于我们｜瓜田日报的品牌定位与内容原则"
        description="了解瓜田日报的品牌介绍、网站定位、发展理念与内容原则：我们只做娱乐话题的整理者，先核实再成稿，错了就改并标注，不拿隐私当卖点。"
        keywords="关于瓜田日报,爆料吃瓜网站介绍,内容原则,娱乐资讯整理,品牌定位"
        canonical="/about"
        ogType="article"
      />
      <JsonLd data={aboutSchema} />
      <JsonLd data={faqSchema} />
      <JsonLd data={breadcrumbSchema} />

      <SiteLayout>
        <PageHero
          eyebrow="关于我们"
          title="把娱乐话题讲清楚，比讲得响亮更重要"
          desc="瓜田日报是一个专注整理娱乐话题的内容站点。我们在意的是信息是否准确、时间线是否清楚，而不是话题够不够刺激。"
          crumbs={[{ label: "关于我们" }]}
        >
          <div className="flex gap-6 text-secondary-50">
            <div>
              <p className="font-heading text-3xl font-black">{siteInfo.since}</p>
              <p className="mt-1 text-xs text-secondary-100/70">开始整理内容</p>
            </div>
            <div>
              <p className="font-heading text-3xl font-black">3 个</p>
              <p className="mt-1 text-xs text-secondary-100/70">内容审核环节</p>
            </div>
          </div>
        </PageHero>

        <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-16 px-4 py-12 md:gap-24 md:px-8 md:py-16">
          <IntroSection />
          <TimelineSection />
          <PrincipleSection />
          <VoiceSection />
          <AboutFaq />
        </div>
      </SiteLayout>
    </>
  );
}